import Axios from 'axios';
import {
  ValidationReason,
  UserValidationReason,
  ValidationResult,
} from '../login-adapter';
import {
  kEmailRegex,
  kUsernameRegex,
  kUsernameValidationEndpoint,
} from '../constants';

// -----------------------------------------------------------------------------
// Shared login form validators, consumed by each app's LoginAdapter. They use
// the shared ValidationReason / UserValidationReason enums so the numeric
// `reason` codes line up with what <LoginSignupForm> switches on, and both apps
// surface the same field-specific errors (the in-app and website flows used to
// drift here — notably the password length error).
const useLoginValidators = () => {
  // ---------------------------------------------------------------------------
  const validateUsername = async (
    username: string,
    validateTaken?: boolean,
  ): Promise<ValidationResult> => {
    if (username.length === 0) {
      return { isValid: false, reason: UserValidationReason.Empty };
    }

    if (username.length < 3 || username.length > 64) {
      return { isValid: false, reason: UserValidationReason.InvalidLength };
    }

    const valid = kUsernameRegex.test(username);
    if (!valid) {
      return { isValid: false, reason: UserValidationReason.Invalid };
    }

    if (validateTaken) {
      try {
        const result = await Axios.get(
          `${kUsernameValidationEndpoint}?UserName=${username}`,
        );
        const taken = result.data;
        if (taken === true) {
          return { isValid: false, reason: UserValidationReason.Taken };
        }
      } catch {
        return { isValid: false, reason: UserValidationReason.Invalid };
      }
    }

    return { isValid: true };
  };

  // ---------------------------------------------------------------------------
  const validateEmail = (email: string): ValidationResult => {
    if (email.length === 0) {
      return { isValid: false, reason: ValidationReason.Empty };
    }

    const valid = kEmailRegex.test(email);
    if (!valid) {
      return { isValid: false, reason: ValidationReason.Invalid };
    }

    return { isValid: true };
  };

  // ---------------------------------------------------------------------------
  const validatePassword = (
    password: string,
    isLogin: boolean,
  ): ValidationResult => {
    if (isLogin) {
      if (!password || password.length === 0) {
        return { isValid: false, reason: ValidationReason.Invalid };
      }

      return { isValid: true };
    }

    if (!password || password.length === 0) {
      return { isValid: false, reason: ValidationReason.Empty };
    }

    if (password.length < 8 || password.length > 64) {
      return { isValid: false, reason: ValidationReason.InvalidLength };
    }

    return { isValid: true };
  };

  // ---------------------------------------------------------------------------
  return {
    validateUsername,
    validateEmail,
    validatePassword,
  };
};

export default useLoginValidators;
